import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React from 'react';
import Measure, { ContentRect } from 'react-measure';
import { brandSecondary } from '../../../styles/colors';
import { Button } from '../../../components-v2/Button';
import { RadioButton } from '../../RadioButton';
import { DisclosureIcon } from '../../svgs/icons/DisclosureIcon';
import { LightBulbIcon } from '../../svgs/icons/LightBulbIcon';
import { styleSheet } from './styles';
export const SocialMediaImagesPreviewAlert = () => {
	return (
		<div className={css(styleSheet.infoWrapper)}>
			<figure>
				<LightBulbIcon fill={brandSecondary} />
			</figure>
			<p>
				This is a preview of the logo overlay. The logo size is an approximation and may vary. The final image will be
				posted to the selected targets.
			</p>
		</div>
	);
};

export const SocialMediaImagesPreviewImages = ({
	images,
	currentImageIndex,
	showOverlayLogo,
	url,
	onImageIndexChange,
	hideWhiteBackground,
	logoSizeValue = Api.DEFAULT_LOGO_SIZE,
}: {
	images: Api.IFileAttachment[];
	currentImageIndex: number;
	showOverlayLogo: boolean;
	url: string;
	onImageIndexChange: (index: number) => () => void;
	hideWhiteBackground?: boolean;
	logoSizeValue?: number;
}) => {
	const logoImageRef = React.useRef<HTMLImageElement>(null);
	const [imageSize, setImageSize] = React.useState({ width: 0, height: 0 });
	const [intrinsicSize, setIntrinsicSize] = React.useState({ width: 0, height: 0 });
	const [logoIntrinsicSize, setLogoIntrinsicSize] = React.useState({ width: 0, height: 0 });
	const [logoSize, setLogoSize] = React.useState<{ width: string | number; height: string | number }>({
		width: 'auto',
		height: 'auto',
	});
	const [logoPadding, setLogoPadding] = React.useState({ padding: '4px 16px' });
	const [logoLoaded, setLogoLoaded] = React.useState(false);

	// Reset logo state when the logo URL changes (e.g. switching impersonation context)
	React.useEffect(() => {
		setLogoIntrinsicSize({ width: 0, height: 0 });
		setLogoLoaded(false);
		setLogoSize({ width: 'auto', height: 'auto' });
		setLogoPadding({ padding: '4px 16px' });
	}, [url]);

	const onContentResize = (contentRect: ContentRect) => {
		setImageSize(prev => ({ ...prev, width: contentRect.client.width, height: contentRect.client.height }));
	};
	const handleImageLoad = React.useCallback((event: React.SyntheticEvent<HTMLImageElement>) => {
		const { naturalWidth, naturalHeight } = event.currentTarget;
		setIntrinsicSize({ width: naturalWidth, height: naturalHeight });
	}, []);

	const handleLogoLoad = React.useCallback((event: React.SyntheticEvent<HTMLImageElement>) => {
		event.preventDefault();
		const { naturalWidth, naturalHeight } = event.currentTarget;
		setLogoIntrinsicSize({ width: naturalWidth, height: naturalHeight });
		setLogoLoaded(true);
	}, []);

	React.useEffect(() => {
		if (intrinsicSize.width === 0 || intrinsicSize.height === 0) return;
		if (logoIntrinsicSize.width === 0 || logoIntrinsicSize.height === 0) return;
		if (!logoLoaded && logoSizeValue === undefined) return;

		// Calculate the scale of the rendered image to the intrinsic size
		const widthScale = imageSize.width / intrinsicSize.width;
		const heightScale = imageSize.height / intrinsicSize.height;

		const logoWidth = logoIntrinsicSize.width;
		const logoHeight = logoIntrinsicSize.height;

		// If the logo is wider or equal to the height, we want to scale the width and adjust the height accordingly
		// If the logo is taller, we want to scale the height and adjust the width accordingly
		const newLogoSize =
			logoWidth < logoHeight
				? { width: 'auto', height: Math.min(logoSizeValue, logoHeight) * heightScale }
				: { width: Math.min(logoSizeValue, logoWidth) * widthScale, height: 'auto' };

		setLogoSize(newLogoSize);
		setLogoPadding({ padding: `${4 * heightScale}px ${16 * widthScale}px` });
		if (logoLoaded) {
			setLogoLoaded(false);
		}
	}, [imageSize, intrinsicSize, logoIntrinsicSize, logoLoaded, logoSizeValue]);

	return (
		<Measure client={true} innerRef={logoImageRef} onResize={onContentResize}>
			{({ measureRef }) => {
				return (
					<div className={css(styleSheet.container, images?.length === 1 ? styleSheet.containerCenter : undefined)}>
						{images?.length > 1 ? (
							<button
								aria-label='Previous image'
								onClick={onImageIndexChange(currentImageIndex === 0 ? images.length - 1 : currentImageIndex - 1)}
							>
								<DisclosureIcon fillColor={brandSecondary} type='chevron' className={css(styleSheet.pointLeft)} />
							</button>
						) : null}
						<figure className={css(styleSheet.imageContainer)}>
							<picture>
								<img
									ref={measureRef}
									src={images?.[currentImageIndex]?.url}
									alt={images?.[currentImageIndex]?.fileName}
									className={css(styleSheet.mainImage)}
									onLoad={handleImageLoad}
								/>
							</picture>

							{showOverlayLogo ? (
								<picture
									className={css(
										styleSheet.logoWrapper,
										hideWhiteBackground ? styleSheet.logoWrapperHideBackground : null
									)}
									style={logoPadding}
								>
									<img
										key={url}
										src={url}
										ref={logoImageRef}
										style={{ ...logoSize }}
										onLoad={handleLogoLoad}
										alt='Account logo overlay'
									/>
								</picture>
							) : null}
						</figure>
						{images?.length > 1 ? (
							<button
								aria-label='Next image'
								onClick={onImageIndexChange(currentImageIndex === images.length - 1 ? 0 : currentImageIndex + 1)}
							>
								<DisclosureIcon fillColor={brandSecondary} type='chevron' className={css(styleSheet.pointRight)} />
							</button>
						) : null}
					</div>
				);
			}}
		</Measure>
	);
};

export const SocialMediaLogoSizeControls = ({
	logoSizeValue,
	contextLogoSize,
	onLogoSizeChange,
	onSaveLogoSize,
	onCancelLogoSize,
}: {
	logoSizeValue: number;
	contextLogoSize: number;
	onLogoSizeChange: (size: number) => void;
	onSaveLogoSize: () => void;
	onCancelLogoSize: () => void;
}) => {
	const hasChanges = logoSizeValue !== contextLogoSize;
	const labelId = 'logo-size-label';

	return (
		<div className={css(styleSheet.logoSizeControls)}>
			<div className={css(styleSheet.sliderHeader)}>
				<span id={labelId}>Logo Size</span>
				<span className={css(styleSheet.sizeValue)} aria-live='polite' aria-atomic='true'>
					{logoSizeValue}px
				</span>
			</div>
			<input
				type='range'
				min={Api.MIN_LOGO_SIZE}
				max={Api.MAX_LOGO_SIZE}
				step={10}
				value={logoSizeValue}
				onChange={e => onLogoSizeChange(parseInt(e.target.value, 10))}
				className={css(styleSheet.slider)}
				aria-labelledby={labelId}
				aria-valuenow={logoSizeValue}
				aria-valuemin={Api.MIN_LOGO_SIZE}
				aria-valuemax={Api.MAX_LOGO_SIZE}
				aria-valuetext={`${logoSizeValue} pixels`}
			/>
			{hasChanges && (
				<div className={css(styleSheet.controlButtons)}>
					<Button onClick={onSaveLogoSize} variant='primary'>
						Save
					</Button>
					<Button onClick={onCancelLogoSize} variant='secondary'>
						Cancel
					</Button>
				</div>
			)}
		</div>
	);
};

export const SocialMediaImagesPreviewRadioButtons = ({
	currentImageIndex,
	images,
	onImageIndexChange,
}: {
	currentImageIndex: number;
	images: Api.IFileAttachment[];
	onImageIndexChange: (index: number) => () => void;
}) => {
	return (
		<div className={`${css(styleSheet.tabNavigation)}`}>
			{images.map((_, i) => {
				return (
					<RadioButton
						checked={i === currentImageIndex || false}
						className={css(styleSheet.circle)}
						id={`images-${i}`}
						key={`images-${i}`}
						onChange={onImageIndexChange(i)}
					/>
				);
			})}
		</div>
	);
};
